/**
 * Display formatting — pure, locale-stable, allocation-cheap.
 *
 * Every function here is deterministic: the locale is pinned to `en-US` so a
 * screenshot taken on any machine reads identically, and `Intl` formatters are
 * built once and reused (constructing one costs ~50µs, which is real money in a
 * 900-row virtualised table).
 *
 * Nothing in this file touches React or the DOM.
 */

import type { FilterCondition } from '@shared/types';

/** Pinned so output never depends on the reviewer's machine. */
export const LOCALE = 'en-US';

/**
 * What an absent value renders as. An en-dash, never an empty cell.
 *
 * The constant is named for the character it actually holds: U+2013 EN DASH.
 * It shipped as `EM_DASH` for a long time while holding this same glyph, so
 * fifteen call sites imported a name that described a character (U+2014) the
 * file has never contained. The glyph is the right one — Linear and Attio both
 * set an empty cell in an en dash, and an em dash at 13px reads as a rule — so
 * the rename moved the name to the value rather than the value to the name.
 */
export const EN_DASH = '–';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/* ── Intl caches ─────────────────────────────────────────────────────────── */

const dateTimeLong = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

const dateMedium = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const dateShort = new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric' });

const dateShortYear = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const timeShort = new Intl.DateTimeFormat(LOCALE, { hour: 'numeric', minute: '2-digit' });

const weekdayShort = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

const currencyCache = new Map<string, Intl.NumberFormat>();

function currencyFormatter(code: string, precision: number): Intl.NumberFormat {
  const cacheKey = `${code}:${precision}`;
  let f = currencyCache.get(cacheKey);
  if (!f) {
    f = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
    currencyCache.set(cacheKey, f);
  }
  return f;
}

const groupedInteger = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/* ── Dates ───────────────────────────────────────────────────────────────── */

export type DateInput = string | number | Date | null | undefined;

/** Coerce anything wire-shaped into a Date, or null when unusable. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface RelativeTimeOptions {
  /** Injectable clock — keeps tests and screenshots deterministic. */
  now?: DateInput;
  /** `'short'` → `2m`; `'long'` → `2 minutes ago`; `'compact'` → `<1h`…`5mo`…`1y`. */
  style?: 'short' | 'long' | 'compact';
  /** What to render when the input is null. */
  fallback?: string;
}

const WEEK = 7 * DAY;
/** Calendar-average month/year — the label is an order of magnitude, not a date. */
const MONTH = 30.44 * DAY;
const YEAR = 365.25 * DAY;

/**
 * One shape for every age: `<1h` `4h` `3d` `3w` `5mo` `1y`. A column of these is
 * sortable by eye, which a column that mixes `6d` with `Jul 30` is not. The
 * absolute timestamp still lives in the tooltip.
 *
 * The sub-hour bucket is deliberately not `9m`: in the same right-aligned column
 * `9m` and `9mo` differ by one character and by a factor of 43,000, which is a
 * misread the scale itself invites. Everything under an hour collapses to `<1h`,
 * so no two rungs of the ladder share a leading letter.
 */
function compactAge(abs: number): string {
  if (abs < HOUR) return '<1h';
  if (abs < DAY) return `${Math.floor(abs / HOUR)}h`;
  if (abs < WEEK) return `${Math.floor(abs / DAY)}d`;
  if (abs < MONTH) return `${Math.floor(abs / WEEK)}w`;
  if (abs < YEAR) return `${Math.max(1, Math.floor(abs / MONTH))}mo`;
  return `${Math.max(1, Math.floor(abs / YEAR))}y`;
}

/**
 * Compact, scannable age: `now` · `2m` · `4h` · `3d` · `Aug 3` · `Aug 3, 2025`.
 * Future instants read `in 2m` / `in 4h`.
 */
export function relativeTime(value: DateInput, options: RelativeTimeOptions = {}): string {
  const { style = 'short', fallback = EN_DASH } = options;
  const date = toDate(value);
  if (!date) return fallback;

  const now = toDate(options.now) ?? new Date();
  const delta = now.getTime() - date.getTime();
  const abs = Math.abs(delta);
  const future = delta < 0;

  if (style === 'compact') {
    const body = compactAge(abs);
    // "in <1h" is a bucket, not a direction; anything inside the hour reads the
    // same whichever side of now it sits on.
    return future && body !== '<1h' ? `in ${body}` : body;
  }

  if (abs < 45_000) return style === 'long' ? 'just now' : 'now';

  if (abs < HOUR) {
    const m = Math.max(1, Math.round(abs / MINUTE));
    return decorate(style === 'long' ? `${m} ${pluralWord(m, 'minute')}` : `${m}m`, future, style);
  }
  if (abs < DAY) {
    const h = Math.floor(abs / HOUR);
    return decorate(style === 'long' ? `${h} ${pluralWord(h, 'hour')}` : `${h}h`, future, style);
  }
  if (abs < 7 * DAY) {
    const d = Math.floor(abs / DAY);
    return decorate(style === 'long' ? `${d} ${pluralWord(d, 'day')}` : `${d}d`, future, style);
  }

  return date.getFullYear() === now.getFullYear()
    ? dateShort.format(date)
    : dateShortYear.format(date);
}

function decorate(body: string, future: boolean, style: NonNullable<RelativeTimeOptions['style']>): string {
  if (future) return `in ${body}`;
  return style === 'long' ? `${body} ago` : body;
}

/**
 * Milliseconds until `relativeTime(value)` would produce a different string, so
 * a live component can schedule one timeout instead of ticking every second.
 * `null` means the label is effectively frozen for this session.
 */
export function relativeTimeRefreshMs(value: DateInput, now: DateInput = undefined): number | null {
  const date = toDate(value);
  if (!date) return null;
  const ref = toDate(now) ?? new Date();
  const abs = Math.abs(ref.getTime() - date.getTime());

  if (abs < 45_000) return 45_000 - abs;
  if (abs < HOUR) return MINUTE - (abs % MINUTE);
  if (abs < DAY) return HOUR - (abs % HOUR);
  if (abs < 7 * DAY) return DAY - (abs % DAY);
  return null;
}

/** Long, unambiguous, timezone-qualified — the tooltip behind every relative time. */
export function absolute(value: DateInput, fallback = EN_DASH): string {
  const date = toDate(value);
  return date ? dateTimeLong.format(date) : fallback;
}

/**
 * `Aug 3, 2026` — date only, no clock.
 *
 * THE RULE, so two screens one click apart never print one field two ways:
 * `absoluteDate` is for a date quoted **once**, as a value in a metadata pair or
 * in prose ("Started: Aug 3, 2026"), where the year is part of the fact.
 * `listDate` is for a date in a **repeating** row or column — a queue, a table,
 * a record's task list — where the year is the same on every line and spends the
 * four widest characters in the cell saying nothing. The full timestamp belongs
 * in the tooltip and the accessible name either way, via `absolute`.
 */
export function absoluteDate(value: DateInput, fallback = EN_DASH): string {
  const date = toDate(value);
  return date ? dateMedium.format(date) : fallback;
}

/**
 * `Jul 17` in the current calendar year, `Dec 3, 2025` outside it — the form a
 * dense list column wants.
 *
 * A due-date column that prints the year on every row spends its four widest
 * characters saying what the row above and the row below already said. Linear,
 * Todoist and Height all drop it. The full timestamp is still what
 * `absolute()` gives the tooltip and the screen reader, so nothing is lost.
 */
export function listDate(value: DateInput, now: DateInput = undefined, fallback = EN_DASH): string {
  const date = toDate(value);
  if (!date) return fallback;
  const ref = toDate(now) ?? new Date();
  return date.getFullYear() === ref.getFullYear()
    ? dateShort.format(date)
    : dateMedium.format(date);
}

/** `2:03 PM`. */
export function clockTime(value: DateInput, fallback = EN_DASH): string {
  const date = toDate(value);
  return date ? timeShort.format(date) : fallback;
}

/** Feed grouping header: `Today` · `Yesterday` · `Mon, Aug 3` · `Aug 3, 2025`. */
export function dayLabel(value: DateInput, now: DateInput = undefined): string {
  const date = toDate(value);
  if (!date) return EN_DASH;
  const ref = toDate(now) ?? new Date();
  const days = calendarDaysBetween(date, ref);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return weekdayShort.format(date);
  return date.getFullYear() === ref.getFullYear() ? dateShort.format(date) : dateMedium.format(date);
}

function calendarDaysBetween(a: Date, b: Date): number {
  const da = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const db = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((db - da) / DAY);
}

/* ── Durations ───────────────────────────────────────────────────────────── */

export interface DurationOptions {
  /** Number of units to show: `2` → `3h 20m`, `1` → `3h`. */
  parts?: number;
  fallback?: string;
}

/** Minutes → `<1m` · `45m` · `3h 20m` · `2d 4h`. */
export function duration(minutes: number | null | undefined, options: DurationOptions = {}): string {
  const { parts = 2, fallback = EN_DASH } = options;
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return fallback;

  const sign = minutes < 0 ? '-' : '';
  const total = Math.abs(minutes);
  if (total < 1) return `${sign}<1m`;

  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = Math.floor(total % 60);

  /*
    `parts` is *adjacent* units, counted from the largest non-zero one — never a
    pair that skips the unit between them. 40,350 minutes is 28d 0h 30m, and
    appending the minutes because the hours happened to be zero printed
    "28d 30m": day-scale magnitude quoted to the minute, with the unit that
    would have made it readable missing. It reads as fake precision because it
    is. Now the run starts at the coarsest unit present and stops at the first
    gap, so the same value is "28d" and every other output is unchanged:
    69 → "1h 9m", 1902.7 → "1d 7h", 46.5 → "46m", 120 → "2h".
  */
  const units: ReadonlyArray<readonly [number, string]> = [
    [d, 'd'],
    [h, 'h'],
    [m, 'm'],
  ];
  const first = units.findIndex(([value]) => value > 0);
  if (first === -1) return `${sign}<1m`;

  const segments = units
    .slice(first, first + Math.max(1, parts))
    .filter(([value], index) => index === 0 || value > 0)
    .map(([value, unit]) => `${value}${unit}`);

  return sign + segments.join(' ');
}

/** Hours → the same shape as {@link duration}. */
export function durationFromHours(
  hours: number | null | undefined,
  options?: DurationOptions,
): string {
  return duration(hours === null || hours === undefined ? hours : hours * 60, options);
}

/** Milliseconds → the same shape as {@link duration}. */
export function durationFromMs(ms: number | null | undefined, options?: DurationOptions): string {
  return duration(ms === null || ms === undefined ? ms : ms / MINUTE, options);
}

/* ── Numbers ─────────────────────────────────────────────────────────────── */

export interface NumberOptions {
  /** Collapse thousands: `12400` → `12.4k`. */
  compact?: boolean;
  /** Fixed decimals. Defaults to "as few as read well". */
  precision?: number;
  fallback?: string;
  /** Force a leading `+` on positive values. */
  sign?: boolean;
}

/** `1,204` · `12.4k` · `1.2M`. Always pair with `.tnum` in the DOM. */
export function compactNumber(
  value: number | null | undefined,
  options: NumberOptions = {},
): string {
  const { compact = true, precision, fallback = EN_DASH, sign = false } = options;
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;

  const prefix = sign && value > 0 ? '+' : '';
  const abs = Math.abs(value);

  if (!compact || abs < 1000) {
    const body =
      precision === undefined
        ? groupedInteger.format(roundTo(value, Number.isInteger(value) ? 0 : 1))
        : value.toFixed(precision);
    return prefix + body;
  }

  const units: Array<[number, string]> = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'k'],
  ];
  for (const [size, suffix] of units) {
    if (abs >= size) {
      const scaled = value / size;
      const digits = precision ?? (Math.abs(scaled) < 100 ? 1 : 0);
      return prefix + trimZeroes(scaled.toFixed(digits)) + suffix;
    }
  }
  return prefix + groupedInteger.format(value);
}

function trimZeroes(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

function roundTo(value: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

export interface PercentOptions {
  /** `100` when the input is a 0–1 fraction (the default), `1` when it is already a %. */
  scale?: 100 | 1;
  precision?: number;
  /** Force a leading `+` on positive values — used by every delta badge. */
  sign?: boolean;
  fallback?: string;
}

/** `0.184` → `18.4%`; with `sign` → `+18.4%`. */
export function percent(value: number | null | undefined, options: PercentOptions = {}): string {
  const { scale = 100, precision = 1, sign = false, fallback = EN_DASH } = options;
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  const scaled = value * scale;
  const prefix = sign && scaled > 0 ? '+' : '';
  return `${prefix}${scaled.toFixed(precision)}%`;
}

export interface CurrencyOptions extends NumberOptions {
  currency?: string;
}

/** `$1,240` · `$1.2M`. Whole units by default — cents are noise in a pipeline view. */
export function currency(
  value: number | null | undefined,
  options: CurrencyOptions = {},
): string {
  const { currency: code = 'USD', compact = false, precision, fallback = EN_DASH } = options;
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;

  if (compact && Math.abs(value) >= 1000) {
    const symbol = currencyFormatter(code, 0).format(0).replace(/[\d.,\s]/g, '');
    const negative = value < 0 ? '-' : '';
    return `${negative}${symbol}${compactNumber(Math.abs(value), { compact: true, precision })}`;
  }
  return currencyFormatter(code, precision ?? 0).format(value);
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** `840 B` · `2.4 MB` · `12 MB`. Binary steps, decimal labels — the file-manager convention. */
export function bytes(value: number | null | undefined, fallback = EN_DASH): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  if (value < 1024) return `${Math.round(value)} B`;

  let n = value;
  let unit = 0;
  while (n >= 1024 && unit < BYTE_UNITS.length - 1) {
    n /= 1024;
    unit += 1;
  }
  return `${trimZeroes(n.toFixed(n < 10 ? 1 : 0))} ${BYTE_UNITS[unit]}`;
}

/* ── Text ────────────────────────────────────────────────────────────────── */

/** `Marta Ruiz` → `MR`; `alphaform` → `AL`; junk → `?`. */
export function initials(name: string | null | undefined, max = 2): string {
  if (!name) return '?';
  const words = name
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .split(/[\s-]+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0]!.slice(0, max).toUpperCase();
  }
  return words
    .slice(0, max)
    .map((w) => w[0]!)
    .join('')
    .toUpperCase();
}

function pluralWord(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

export interface PluraliseOptions {
  /** `false` → returns just the word. */
  includeCount?: boolean;
  /** Compact the count: `12.4k inquiries`. */
  compact?: boolean;
  /** Render `0` as this word: `pluralise(0, 'task', undefined, { zero: 'No' })` → `No tasks`. */
  zero?: string;
}

/** `1 inquiry` · `3 inquiries` · `No tasks`. */
export function pluralise(
  count: number,
  singular: string,
  plural?: string,
  options: PluraliseOptions = {},
): string {
  const { includeCount = true, compact = false, zero } = options;
  const word = pluralWord(count, singular, plural);
  if (!includeCount) return word;
  if (count === 0 && zero) return `${zero} ${word}`;
  return `${compact ? compactNumber(count) : groupedInteger.format(count)} ${word}`;
}

/** `structural-analysis-rev-4-final.pdf` → `structural-an…-final.pdf`. */
export function truncateMiddle(value: string | null | undefined, max = 24, ellipsis = '…'): string {
  if (!value) return '';
  if (value.length <= max) return value;
  if (max <= ellipsis.length + 2) return value.slice(0, Math.max(1, max - 1)) + ellipsis;

  const keep = max - ellipsis.length;
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${value.slice(0, head)}${ellipsis}${value.slice(value.length - tail)}`;
}

/** Sentence-case a machine key: `stage_changed` → `Stage changed`. */
export function humanise(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Join with an Oxford-comma-free `and`: `Ana, Ben and Cai`. */
export function joinList(values: string[], conjunction = 'and'): string {
  if (values.length === 0) return '';
  if (values.length === 1) return values[0]!;
  return `${values.slice(0, -1).join(', ')} ${conjunction} ${values[values.length - 1]!}`;
}

/* ── Filter values ───────────────────────────────────────────────────────── */

export type FilterValueKind =
  | 'text'
  | 'number'
  | 'date'
  | 'minutes'
  | 'percent'
  | 'bytes'
  | 'score'
  | 'currency';

export interface FilterValueOptions {
  kind?: FilterValueKind;
  /** Turn an id into a label (owner id → `Marta Ruiz`). */
  resolve?: (value: string | number) => string | undefined;
  /** How many list members to name before collapsing to `+N`. */
  maxListed?: number;
  operator?: FilterCondition['operator'];
}

/**
 * Render a filter condition's value the way a human would say it:
 * `Qualifying`, `70`, `Aug 3, 2026`, `Marta Ruiz, Ana Oyelaran +2`.
 */
export function formatFilterValue(
  value: FilterCondition['value'],
  options: FilterValueOptions = {},
): string {
  const { kind = 'text', resolve, maxListed = 2, operator } = options;

  if (operator === 'is_empty') return 'empty';
  if (operator === 'is_not_empty') return 'not empty';
  if (value === null || value === undefined || value === '') return EN_DASH;

  if (Array.isArray(value)) {
    if (value.length === 0) return EN_DASH;
    const labels = value.map((v) => formatScalar(v, kind, resolve));
    if (operator === 'between' && labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    if (labels.length <= maxListed) return joinList(labels);
    return `${labels.slice(0, maxListed).join(', ')} +${labels.length - maxListed}`;
  }

  return formatScalar(value, kind, resolve);
}

function formatScalar(
  value: string | number | boolean,
  kind: FilterValueKind,
  resolve?: (value: string | number) => string | undefined,
): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  const resolved = resolve?.(value);
  if (resolved !== undefined) return resolved;

  switch (kind) {
    case 'date':
      return absoluteDate(typeof value === 'number' ? new Date(value) : value, String(value));
    case 'minutes':
      return duration(Number(value));
    case 'percent':
      return percent(Number(value), { scale: 1, precision: 0 });
    case 'bytes':
      return bytes(Number(value));
    case 'currency':
      // The chip reads `Deal value ≥ $1.0M`, in the same voice the analytics
      // tile and the grid cell print the field.
      return currency(Number(value), { compact: true });
    case 'number':
    case 'score':
      return compactNumber(Number(value), { compact: false });
    default:
      return String(value);
  }
}

/* ── Namespace ───────────────────────────────────────────────────────────── */

/**
 * Grouped access for call sites that read better qualified —
 * `fmt.number(row.score)`, `fmt.relativeTime(row.createdAt)`.
 */
export const fmt = {
  relativeTime,
  relativeTimeRefreshMs,
  absolute,
  absoluteDate,
  listDate,
  clockTime,
  dayLabel,
  duration,
  durationFromHours,
  durationFromMs,
  number: compactNumber,
  percent,
  currency,
  bytes,
  initials,
  pluralise,
  truncateMiddle,
  humanise,
  joinList,
  filterValue: formatFilterValue,
  toDate,
} as const;
